/**
 * Pagination - Page navigation component for paginated list views.
 *
 * Renders Previous / page number buttons / Next controls. Page numbers are
 * displayed with ellipsis for large page counts (e.g., 1 2 ... 5 6 7 ... 20).
 * Buttons are disabled at the boundaries (no Previous on page 1, no Next on
 * the last page).
 *
 * Enforces min-h-44px touch targets for mobile accessibility.
 *
 * @example
 * <Pagination currentPage={3} totalPages={10} onPageChange={setPage} />
 */
import React from 'react';

/**
 * Props for the Pagination component.
 *
 * @property currentPage  - The currently active page (1-indexed)
 * @property totalPages	  - Total number of pages available
 * @property onPageChange - Callback fired with the new page number when a button is clicked
 */
export interface PaginationProps {
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
	/** Optional extra classes for the outer wrapper. */
	className?: string;
}

/**
 * Generates the array of page numbers to display, including ellipsis markers.
 * Shows at most 7 items: first page, last page, current page +/- 1, and ellipsis.
 *
 * @param current - Current page number
 * @param total	  - Total number of pages
 * @returns Array of page numbers and -1 values representing ellipsis
 */
function getPageNumbers(current: number, total: number): number[] {
	/* If total pages fit in 7 slots, show them all without ellipsis */
	if (total <= 7) {
		return Array.from({ length: total }, (_, i) => i + 1);
	}

	const pages: number[] = [];

	/* Always include page 1 */
	pages.push(1);

	/* Add left ellipsis if current page is far from the start */
	if (current > 3) {
		pages.push(-1); // -1 represents ellipsis
	}

	/* Add pages around the current page (current-1, current, current+1) */
	const start = Math.max(2, current - 1);
	const end = Math.min(total - 1, current + 1);
	for (let i = start; i <= end; i++) {
		pages.push(i);
	}

	/* Add right ellipsis if current page is far from the end */
	if (current < total - 2) {
		pages.push(-1); // -1 represents ellipsis
	}

	/* Always include the last page */
	pages.push(total);

	return pages;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange, className = '' }) => {
	/* Don't render pagination if there's only one page or no pages */
	if (totalPages <= 1) {
		return null;
	}

	/* Generate the page number array with ellipsis markers */
	const pageNumbers = getPageNumbers(currentPage, totalPages);

	return (
		/* Centered pagination bar with top margin for spacing from content above */
		<nav className={`pagination flex items-center justify-center gap-4px mt-24px ${className}`} data-ref="molecule-pagination" aria-label="Pagination">
			{/* Previous button — disabled on page 1 */}
			<button type="button" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="inline-flex items-center justify-center px-12px py-8px text-14px font-sans font-medium rounded-md min-h-44px text-brand-text-muted hover:bg-brand-surface hover:text-brand-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Go to previous page">
				{/* Left chevron icon */}
				<svg className="h-16px w-16px mr-4px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
					<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
				</svg>
				Previous
			</button>

			{/* Page number buttons and ellipsis indicators */}
			{pageNumbers.map((page, index) => {
				/* Render ellipsis (non-interactive) for -1 markers */
				if (page === -1) {
					return (
						<span key={`ellipsis-${index}`} className="inline-flex items-center justify-center w-40px h-44px text-14px font-sans text-brand-text-muted" aria-hidden="true">
							...
						</span>
					);
				}

				/* Determine if this page button is the active/current page */
				const isActive = page === currentPage;

				return (
					<button key={page} type="button" onClick={() => onPageChange(page)} disabled={isActive} className={[
						'pagination__page',
						'inline-flex items-center justify-center w-40px h-44px text-14px font-sans font-medium rounded-md transition-colors',
						isActive
							? 'pagination__page--active bg-brand-primary text-white cursor-default'
							: 'text-brand-text-muted hover:bg-brand-surface hover:text-brand-primary',
					].join(' ')} aria-label={`Go to page ${page}`} aria-current={isActive ? 'page' : undefined}>
						{page}
					</button>
				);
			})}

			{/* Next button — disabled on the last page */}
			<button type="button" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="inline-flex items-center justify-center px-12px py-8px text-14px font-sans font-medium rounded-md min-h-44px text-brand-text-muted hover:bg-brand-surface hover:text-brand-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Go to next page">
				Next
				{/* Right chevron icon */}
				<svg className="h-16px w-16px ml-4px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
					<path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
				</svg>
			</button>
		</nav>
	);
};

export default Pagination;
